/*
 * Gates the checkout button until the customer has accepted that digital
 * products lose the right of withdrawal once delivery starts.
 *
 * The checkbox is a real form field (name="attributes[...]"), so the consent is
 * recorded on the order by the plain form POST even if this script never runs.
 * On top of that it is written to the cart with /cart/update.js, which is what
 * lets the ticked state survive a Section Rendering API refresh of the drawer.
 */
// Survives every re-render because nothing here lives in the DOM: a section
// render can land before /cart/update.js has written the tick, and without this
// the customer's flueben silently disappears.
window.digitalConsentMemory = window.digitalConsentMemory || {};

if (!customElements.get('digital-consent')) {
  customElements.define(
    'digital-consent',
    class DigitalConsent extends HTMLElement {
      connectedCallback() {
        this.abortController = new AbortController();
        this.attributeName = this.dataset.attribute;
        this.pendingSaves = 0;
        this.active = this.dataset.active === 'true';

        // Resolved before the early return: the button and the express container
        // sit OUTSIDE this element, so on /cart they survive the re-render that
        // drops the gate and would stay blocked forever if nobody released them.
        this.buttons = Array.from(document.querySelectorAll(this.dataset.checkoutSelector));
        this.expressContainers = this.dataset.expressSelector
          ? Array.from(document.querySelectorAll(this.dataset.expressSelector))
          : [];

        if (!this.active) {
          // The cart no longer holds a digital product, so drop a stale consent.
          delete window.digitalConsentMemory[this.attributeName];
          this.releaseGate();
          if (this.dataset.stored === 'true') this.save('');
          return;
        }

        this.checkbox = this.querySelector('.digital-consent__checkbox');
        this.errorElement = this.querySelector('.digital-consent__error');
        if (!this.checkbox) {
          this.releaseGate();
          return;
        }

        this.form = document.getElementById(this.dataset.form);

        const { signal } = this.abortController;

        this.checkbox.addEventListener('change', this.onChange.bind(this), { signal });

        // Pressing Enter in the quantity field submits implicitly, and native
        // constraint validation wins over any submit listener. This is the only
        // place our own Danish wording can replace the browser's bubble.
        this.checkbox.addEventListener(
          'invalid',
          (event) => {
            event.preventDefault();
            this.showError();
          },
          { signal }
        );

        this.buttons.forEach((button) => {
          button.addEventListener('click', this.onCheckoutAttempt.bind(this), { signal, capture: true });
        });

        if (this.form) {
          this.form.addEventListener('submit', this.onCheckoutAttempt.bind(this), { signal, capture: true });
        }

        // On /cart the drawer and the cart page each render their own checkbox.
        // Ticking one has to release the other, or the header cart icon leads
        // back to a blocked button after the customer already consented.
        document.addEventListener('digital-consent:change', this.onPeerChange.bind(this), { signal });

        this.restoreFromMemory();
        this.render();
      }

      restoreFromMemory() {
        const remembered = window.digitalConsentMemory[this.attributeName];
        if (!remembered || this.checkbox.checked) return;

        this.checkbox.checked = true;
        this.checkbox.value = remembered;
        this.save(remembered);
      }

      releaseGate() {
        this.buttons.forEach((button) => {
          button.classList.remove('button--consent-blocked');
          button.removeAttribute('aria-disabled');
        });
        this.expressContainers.forEach((container) => {
          container.classList.remove('digital-consent-blocked');
          container.removeAttribute('inert');
        });
      }

      disconnectedCallback() {
        // The drawer replaces this element wholesale on every cart refresh, so the
        // listeners left on the checkout button outside it have to go with it.
        if (this.abortController) this.abortController.abort();
      }

      get accepted() {
        return Boolean(this.checkbox && this.checkbox.checked);
      }

      onChange() {
        if (this.accepted) {
          this.checkbox.value = this.stampedValue();
          window.digitalConsentMemory[this.attributeName] = this.checkbox.value;
          this.hideError();
        } else {
          delete window.digitalConsentMemory[this.attributeName];
        }
        const value = this.accepted ? this.checkbox.value : '';
        this.save(value);
        this.render();

        document.dispatchEvent(
          new CustomEvent('digital-consent:change', {
            detail: { source: this, attribute: this.attributeName, value: value },
          })
        );
      }

      onPeerChange(event) {
        const detail = event.detail || {};
        if (detail.source === this || detail.attribute !== this.attributeName) return;
        if (!this.checkbox) return;

        const accepted = Boolean(detail.value);
        if (this.checkbox.checked === accepted) return;

        this.checkbox.checked = accepted;
        if (accepted) {
          this.checkbox.value = detail.value;
          this.hideError();
        }
        this.render();
      }

      onCheckoutAttempt(event) {
        if (this.accepted) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.showError();
        this.checkbox.focus({ preventScroll: true });
        this.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }

      render() {
        // save() calls this, and save() also runs on the inactive branch that
        // clears a stale consent, where there is nothing to gate.
        if (!this.active || !this.checkbox) return;

        const accepted = this.accepted;

        this.buttons.forEach((button) => {
          button.classList.toggle('button--consent-blocked', !accepted);
          button.setAttribute('aria-disabled', String(!accepted));
        });

        // Express buttons cannot be intercepted reliably, so they stay inert until
        // the consent has actually been written to the cart.
        const expressReady = accepted && this.pendingSaves === 0;
        this.expressContainers.forEach((container) => {
          container.classList.toggle('digital-consent-blocked', !expressReady);
          if (expressReady) {
            container.removeAttribute('inert');
          } else {
            container.setAttribute('inert', '');
          }
        });

        this.classList.toggle('digital-consent--accepted', accepted);
      }

      stampedValue() {
        const now = new Date();
        const pad = (value) => String(value).padStart(2, '0');
        const date = `${pad(now.getDate())}.${pad(now.getMonth() + 1)}.${now.getFullYear()}`;
        const time = `${pad(now.getHours())}.${pad(now.getMinutes())}`;

        // The customer's clock, not the shop's, so the offset has to be spelled
        // out or the stamp cannot be compared with the order's own timestamp.
        const minutes = -now.getTimezoneOffset();
        const abs = Math.abs(minutes);
        const zone =
          'UTC' + (minutes < 0 ? '-' : '+') + pad(Math.floor(abs / 60)) + (abs % 60 ? ':' + pad(abs % 60) : '');

        return `${this.dataset.acceptedPrefix} ${date} kl. ${time} (${zone})`;
      }

      save(value) {
        if (!window.routes || !window.routes.cart_update_url || typeof fetchConfig !== 'function') return;

        const body = JSON.stringify({ attributes: { [this.attributeName]: value } });

        this.pendingSaves += 1;
        fetch(window.routes.cart_update_url, { ...fetchConfig(), body })
          .catch((error) => console.error(error))
          .finally(() => {
            this.pendingSaves -= 1;
            if (this.isConnected) this.render();
          });

        this.render();
      }

      showError() {
        if (!this.errorElement || this.errorElement.textContent === this.dataset.error) return;
        this.errorElement.textContent = this.dataset.error;
      }

      hideError() {
        if (!this.errorElement) return;
        this.errorElement.textContent = '';
      }
    }
  );
}
